import { useState, useEffect, useRef, useCallback } from "react";
import { API_BASE } from "@/config/api";
import { Asset, CompanySearchResult, AssetListing } from "@/schemas";
import { X, ChevronDown, Search, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AssetLogo } from "@/components/asset/asset-logo";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchWithAuth } from "@/lib/api";
import { getExchangeLabel } from "@/lib/exchange-mapping";
import { selectPreferredAsset, selectPreferredAssetId } from "@/lib/preferred-asset";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface AssetSearchProps {
  value?: string;
  onSelect: (asset: Asset | null) => void;
  placeholder?: string;
  assetType: string; // expected to be a comma-separated string e.g. "type1,type2,type3"
  enableCompanyFlow?: boolean; // Enable company-first selection for stocks/ETFs
}

interface GroupedAssets {
  symbol: string;
  name: string;
  exchanges: string[];
  assets: Asset[];
}

type SelectionStep = "search" | "currency" | "exchange";

export function AssetSearch({
  value,
  onSelect,
  placeholder,
  assetType,
  enableCompanyFlow = false,
}: AssetSearchProps) {
  const [query, setQuery] = useState("");
  const [searchBy, setSearchBy] = useState<"" | "symbol" | "name" | "isin">("");
  const [searchResults, setSearchResults] = useState<Asset[]>([]);
  const [companyResults, setCompanyResults] = useState<CompanySearchResult[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupedAssets | null>(null);
  const [selectedExchange, setSelectedExchange] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Company flow state
  const [step, setStep] = useState<SelectionStep>("search");
  const [selectedCompany, setSelectedCompany] = useState<CompanySearchResult | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [filteredListings, setFilteredListings] = useState<AssetListing[]>([]);

  const searchByLabels: Record<string, string> = {
    "": "All",
    "symbol": "Ticker",
    "name": "Name",
    "isin": "ISIN",
  };

  // Check if we should use company flow (only for stock/etf types)
  const shouldUseCompanyFlow = enableCompanyFlow && 
    (assetType.includes("stock") || assetType.includes("etf")) &&
    !assetType.includes("crypto") &&
    !assetType.includes("fund");

  // Helper to get default ticker for a company (prefer US exchange, otherwise first)
  const getDefaultTicker = (company: CompanySearchResult): string | null => {
    return selectPreferredAsset(company.listings)?.symbol || null;
  };

  // Groupe les assets par symbol+name (legacy flow)
  const groupAssets = (assets?: Asset[]): GroupedAssets[] => {
    const groups = (assets ?? []).reduce((acc, asset) => {
      const key = `${asset.symbol}-${asset.name}`;
      if (!acc[key]) {
        acc[key] = {
          symbol: asset.symbol,
          name: asset.name,
          exchanges: [],
          assets: [],
        } as GroupedAssets;
      }
      if ("exchange" in asset && asset.exchange) {
        acc[key].exchanges.push(asset.exchange);
      }
      acc[key].assets.push(asset);
      return acc;
    }, {} as Record<string, GroupedAssets>);

    return Object.values(groups);
  };

  useEffect(() => {
    const fetchAssets = async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        setCompanyResults([]);
        return;
      }

      try {
        const types = assetType
          .split(",")
          .map((t) => t.trim())
          .join(",");
        const url = new URL(`${API_BASE}/assets/search`);
        url.searchParams.append("asset_types", types);
        url.searchParams.append("q", searchQuery);
        if (searchBy) {
          url.searchParams.append("search_by", searchBy);
        }
        
        // Use company grouping for stock/etf when enabled
        if (shouldUseCompanyFlow) {
          url.searchParams.append("group_by", "company");
        }

        const response = await fetchWithAuth(url.toString(), {
          method: "GET",
        });
        const data = await response.json();
        
        if (shouldUseCompanyFlow) {
          setCompanyResults(data.companies || []);
          setSearchResults([]);
        } else {
          setSearchResults(data.results || []);
          setCompanyResults([]);
        }
      } catch (error) {
        console.error("Error fetching assets:", error);
        setSearchResults([]);
        setCompanyResults([]);
      }
    };

    const debounce = setTimeout(() => {
      fetchAssets(query);
    }, 300);

    return () => clearTimeout(debounce);
  }, [query, assetType, searchBy, shouldUseCompanyFlow]);

  // Handle click outside to close (but ignore Radix portal clicks)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Ignore clicks inside Radix portal (dropdown menu)
      const radixPortal = document.querySelector('[data-radix-portal]');
      const radixPopper = document.querySelector('[data-radix-popper-content-wrapper]');
      if (radixPortal?.contains(target) || radixPopper?.contains(target)) {
        return;
      }
      
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Company flow handlers - defined before useEffect that uses them
  const handleListingSelect = useCallback(async (listing: AssetListing) => {
    // Fetch the full asset details
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/assets/${listing.asset_id}`,
        { method: "GET" }
      );
      const asset = await response.json();
      setSelectedAsset(asset);
      onSelect(asset);
    } catch (error) {
      console.error("Error fetching asset details:", error);
    }
  }, [onSelect]);

  useEffect(() => {
    const syncSelectedAsset = async () => {
      if (!value) {
        setSelectedAsset(null);
        setSelectedGroup(null);
        setSelectedExchange(null);
        setSelectedCompany(null);
        setSelectedCurrency(null);
        setFilteredListings([]);
        setStep("search");
        return;
      }

      if (selectedAsset?.asset_id === value) {
        return;
      }

      try {
        const response = await fetchWithAuth(`${API_BASE}/assets/${value}`, {
          method: "GET",
        });
        if (!response.ok) {
          return;
        }

        const asset = await response.json();
        setSelectedAsset(asset);
      } catch (error) {
        console.error("Error fetching selected asset:", error);
      }
    };

    void syncSelectedAsset();
  }, [selectedAsset?.asset_id, value]);

  // Filter listings when currency changes (company flow)
  useEffect(() => {
    if (selectedCompany && selectedCurrency) {
      const filtered = selectedCompany.listings.filter(
        (l) => l.currency_id === selectedCurrency
      );
      setFilteredListings(filtered);
      
      // Auto-select if only one listing
      if (filtered.length === 1) {
        handleListingSelect(filtered[0]);
      }
    }
  }, [selectedCurrency, selectedCompany, handleListingSelect]);

  const handleCompanySelect = (company: CompanySearchResult) => {
    setSelectedCompany(company);
    setStep("currency");
    setIsFocused(false);
    
    // Auto-select currency if only one
    if (company.currencies.length === 1) {
      setSelectedCurrency(company.currencies[0].currency_id);
    }
  };

  const handleCurrencySelect = (currencyId: string) => {
    setSelectedCurrency(currencyId);
    if (selectedCompany) {
      const filtered = selectedCompany.listings.filter(
        (l) => l.currency_id === currencyId
      );
      setFilteredListings(filtered);
      
      // Auto-select if only one listing, otherwise show exchange selector
      if (filtered.length === 1) {
        handleListingSelect(filtered[0]);
      } else {
        setStep("exchange");
      }
    }
  };

  const handleExchangeSelect = (exchange: string) => {
    setSelectedExchange(exchange);
    if (selectedGroup) {
      const matchingAsset = selectedGroup.assets.find(
        (asset) =>
          (asset.type === "stock" || asset.type === "etf" || asset.type === "fund") &&
          asset.exchange === exchange
      );
      if (matchingAsset) {
        setSelectedAsset(matchingAsset);
        onSelect(matchingAsset);
      }
    }
  };

  const handleGroupSelect = (group: GroupedAssets) => {
    setSelectedGroup(group);
    const isCryptoGroup = group.assets.every((asset) => asset.type === "crypto");
    if (isCryptoGroup) {
      setSelectedExchange(null);
      const cryptoAsset = group.assets[0];
      if (cryptoAsset) {
        setSelectedAsset(cryptoAsset);
        onSelect(cryptoAsset);
      }
      return;
    }

    const preferredAssetId = selectPreferredAssetId(group.assets, selectedAsset?.asset_id);
    const preferredAsset = group.assets.find((asset) => asset.asset_id === preferredAssetId);

    if (preferredAsset) {
      setSelectedExchange(
        "exchange" in preferredAsset ? preferredAsset.exchange || null : null,
      );
      setSelectedAsset(preferredAsset);
      onSelect(preferredAsset);
      return;
    }

    if (group.assets.length === 1) {
      const singleAsset = group.assets[0];
      if (singleAsset) {
        setSelectedExchange("exchange" in singleAsset ? singleAsset.exchange || null : null);
        setSelectedAsset(singleAsset);
        onSelect(singleAsset);
      }
    }
  };

  const handleClear = () => {
    setSelectedAsset(null);
    setSelectedGroup(null);
    setSelectedExchange(null);
    setSelectedCompany(null);
    setSelectedCurrency(null);
    setFilteredListings([]);
    setStep("search");
    setQuery("");
    onSelect(null);
  };

  const handleBackToSearch = () => {
    setSelectedAsset(null);
    setSelectedCompany(null);
    setSelectedCurrency(null);
    setFilteredListings([]);
    setStep("search");
  };

  const renderSelectedAsset = () => {
    if (!selectedAsset) {
      return null;
    }

    const exchange =
      "exchange" in selectedAsset && selectedAsset.exchange
        ? getExchangeLabel(selectedAsset.exchange) || selectedAsset.exchange
        : null;

    return (
      <div className="rounded-lg border shadow-md flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <AssetLogo asset={selectedAsset} size="sm" />
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium">{selectedAsset.symbol}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
                {selectedAsset.type}
              </span>
            </div>
            <span className="text-sm text-muted-foreground truncate">
              {selectedAsset.name}
              {exchange ? ` - ${exchange}` : ""}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedAsset(null);
              setQuery("");
              setIsFocused(true);
              setStep("search");
            }}
            className="h-8 px-2 text-xs"
          >
            Change
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="h-8 px-2"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  // Render company flow UI
  const renderCompanyFlow = () => {
    if (selectedAsset) {
      return renderSelectedAsset();
    }

    if (step === "search") {
      return renderSearchInput();
    }

    if (selectedCompany) {
      return (
        <div className="space-y-3">
          {/* Selected company header */}
          <div className="rounded-lg border shadow-md flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{selectedCompany.company_name}</span>
                  {getDefaultTicker(selectedCompany) && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                      {getDefaultTicker(selectedCompany)}
                    </span>
                  )}
                </div>
                {selectedCompany.isin && (
                  <span className="text-xs text-muted-foreground">
                    ISIN: {selectedCompany.isin}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToSearch}
                className="h-8 px-2 text-xs"
              >
                Change
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-8 px-2"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Currency selector */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-muted-foreground">
              Trading Currency
            </label>
            <Select
              value={selectedCurrency || ""}
              onValueChange={handleCurrencySelect}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {selectedCompany.currencies.map((currency) => (
                  <SelectItem key={currency.currency_id} value={currency.currency_id}>
                    <div className="flex items-center justify-between w-full gap-4">
                      <span className="font-medium">{currency.symbol}</span>
                      <span className="text-xs text-muted-foreground">
                        {currency.count} exchange{currency.count > 1 ? "s" : ""}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Exchange selector (only if multiple listings for selected currency) */}
          {step === "exchange" && filteredListings.length > 1 && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">
                Exchange
              </label>
              <Select
                onValueChange={(assetId) => {
                  const listing = filteredListings.find((l) => l.asset_id === assetId);
                  if (listing) {
                    handleListingSelect(listing);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select exchange" />
                </SelectTrigger>
                <SelectContent>
                  {filteredListings.map((listing) => (
                    <SelectItem key={listing.asset_id} value={listing.asset_id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{listing.symbol}</span>
                        <span className="text-muted-foreground">-</span>
                        <span>{getExchangeLabel(listing.exchange) || listing.exchange}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  // Render search input
  const renderSearchInput = () => (
    <div className="space-y-0">
      <div 
        className={cn(
          "flex items-center gap-2 rounded-lg border bg-background transition-all",
          isFocused && "ring-1 ring-ring"
        )}
      >
        <Search className="h-4 w-4 text-muted-foreground ml-3 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          className="flex-1 h-10 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          placeholder={placeholder || "Search assets..."}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
        />

        <div className="mr-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 px-2.5 text-xs font-medium gap-1 border-border/70 bg-muted/40 hover:bg-muted/70",
                  searchBy !== "" && "border-primary/50 text-foreground bg-primary/5",
                )}
              >
                {searchByLabels[searchBy]}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <DropdownMenuItem onClick={() => {
                setSearchBy("");
                setIsFocused(true);
                // Refocus input to trigger search with new filter
                setTimeout(() => inputRef.current?.focus(), 0);
              }}>
                All fields
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setSearchBy("symbol");
                setIsFocused(true);
                // Refocus input to trigger search with new filter
                setTimeout(() => inputRef.current?.focus(), 0);
              }}>
                Ticker/Symbol
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setSearchBy("name");
                setIsFocused(true);
                // Refocus input to trigger search with new filter
                setTimeout(() => inputRef.current?.focus(), 0);
              }}>
                Name
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setSearchBy("isin");
                setIsFocused(true);
                // Refocus input to trigger search with new filter
                setTimeout(() => inputRef.current?.focus(), 0);
              }}>
                ISIN
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Results */}
      {isFocused && query.length >= 2 && (
        <Command className="rounded-lg border shadow-md mt-1" shouldFilter={false}>
          <CommandList>
            <CommandEmpty>No assets found.</CommandEmpty>
            <CommandGroup>
              {shouldUseCompanyFlow ? (
                // Company-grouped results
                companyResults.map((company) => (
                  <CommandItem
                    key={company.isin || company.company_name}
                    value={company.isin || company.company_name}
                    onSelect={() => {
                      handleCompanySelect(company);
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Building2 className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">
                            {company.company_name}
                          </span>
                          {getDefaultTicker(company) && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                              {getDefaultTicker(company)}
                            </span>
                          )}
                        </div>
                        {company.isin && (
                          <span className="text-xs text-muted-foreground">
                            ISIN: {company.isin}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-2 shrink-0">
                      {company.currencies.map((currency) => (
                        <Badge 
                          key={currency.currency_id} 
                          variant="secondary"
                          className="text-xs px-1.5 py-0"
                        >
                          {currency.symbol}
                        </Badge>
                      ))}
                    </div>
                  </CommandItem>
                ))
              ) : (
                // Legacy symbol+name grouped results
                groupAssets(searchResults).map((group) => (
                  <CommandItem
                    key={`${group.symbol}-${group.name}`}
                    value={`${group.symbol}-${group.name}`}
                    onSelect={() => {
                      handleGroupSelect(group);
                      setIsFocused(false);
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {group.assets[0] && <AssetLogo asset={group.assets[0]} size="sm" />}
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{group.symbol}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
                            {group.assets[0]?.type}
                          </span>
                        </div>
                        <span className="text-sm text-muted-foreground truncate">
                          {group.name}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground ml-auto shrink-0">
                      {group.exchanges.length > 0 
                        ? `${group.exchanges.length} exchange${group.exchanges.length > 1 ? "s" : ""}`
                        : ""
                      }
                    </span>
                  </CommandItem>
                ))
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      )}
    </div>
  );

  // Render legacy flow (for non-company mode)
  const renderLegacyFlow = () => {
    if (selectedAsset) {
      return renderSelectedAsset();
    }

    if (selectedGroup) {
      return (
        <>
          <div className="rounded-lg border shadow-md flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              {selectedGroup.assets[0] && <AssetLogo asset={selectedGroup.assets[0]} size="sm" />}
              <span className="font-medium">{selectedGroup.symbol}</span>
              <span className="text-sm text-muted-foreground">
                - {selectedGroup.name}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="h-8 px-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {selectedGroup.assets.some((asset) => asset.type !== "crypto") &&
            selectedGroup.exchanges.length > 0 && (
            <Select
              value={selectedExchange || ""}
              onValueChange={(exchange) => {
                handleExchangeSelect(exchange);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select exchange" />
              </SelectTrigger>
              <SelectContent>
                {selectedGroup.exchanges.map((exchange) => (
                  <SelectItem key={exchange} value={exchange}>
                    {getExchangeLabel(exchange) || exchange}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </>
      );
    }

    return renderSearchInput();
  };

  return (
    <div ref={containerRef} className="relative space-y-2 p-0.5">
      {shouldUseCompanyFlow ? renderCompanyFlow() : renderLegacyFlow()}
    </div>
  );
}
